import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import { ThemeProvider } from "next-themes";
import { lazy, Suspense } from "react";
import Index from "./pages/Index";
import { AppSkeleton } from "./components/AppSkeleton";
import { useServiceWorkerUpdate } from "./hooks/useServiceWorkerUpdate";

// Lazy load non-critical pages to reduce initial bundle size
const Auth = lazy(() => import("./pages/Auth"));
const RecallDetail = lazy(() => import("./pages/RecallDetail"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const Settings = lazy(() => import("./pages/Settings"));
const About = lazy(() => import("./pages/About"));
const Admin = lazy(() => import("./pages/Admin"));
const StyleGuide = lazy(() => import("./pages/StyleGuide"));
const Confidentialite = lazy(() => import("./pages/Confidentialite"));
const CGU = lazy(() => import("./pages/CGU"));
const MentionsLegales = lazy(() => import("./pages/MentionsLegales"));
const Install = lazy(() => import("./pages/Install"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Optimized QueryClient with smart caching defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,      // Data considered fresh for 5 minutes
      gcTime: 30 * 60 * 1000,        // Cache kept for 30 minutes
      refetchOnWindowFocus: false,   // Prevent unnecessary refetches
      retry: 1,                       // Single retry on failure
      refetchOnMount: false,         // Use cache when available
    },
  },
});

// Inner component to use hooks
const AppContent = () => {
  useServiceWorkerUpdate();
  
  return (
    <BrowserRouter>
      <Suspense fallback={<AppSkeleton />}>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/recall/:id" element={<RecallDetail />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/onboarding" element={<Onboarding />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/about" element={<About />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/style-guide" element={<StyleGuide />} />
          <Route path="/confidentialite" element={<Confidentialite />} />
          <Route path="/cgu" element={<CGU />} />
          <Route path="/mentions-legales" element={<MentionsLegales />} />
          <Route path="/install" element={<Install />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
};

const App = () => (
  <HelmetProvider>
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem storageKey="miamsecur-theme">
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <AppContent />
        </TooltipProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </HelmetProvider>
);

export default App;
